import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { addNotification } from 'reapop';
import { connect, useSelector } from 'react-redux';
import { find, get, isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { CircularProgress, Dialog, FlatButton, FontIcon, MenuItem, Paper, RaisedButton, SelectField, TextField } from 'material-ui';
import { useForm, Controller } from 'react-hook-form/dist/index.ie11';
import styles from 'styles/TBenefits';
import Thinking from '../../images/Thinking';
import KTitle from '../../components/KJRN/KTitle';
import { custom } from '../../utils/Api';
import { getWorkingHoursToday } from '../../sites/time/api';

import { isHalfDay } from '../../sites/time/components/holidays';

import pallet from '../../constants/colors';
import holidayResponseStatuses from '../../constants/holidayResponseStatuses';

const errorStyle = {
  color: 'red',
  fontSize: '12px',
  margintop: 20,
};

const warningStyle = {
  color: 'orange',
  fontSize: '12px',
  margintop: 20,
};

const { OK, WARNING, ERROR } = holidayResponseStatuses;

const useWorkingHours = (detailId) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [inKjarniTime, setInKjarniTime] = useState(false);
  useEffect(() => {
    const fetchWorkingHours = async () => {
      setLoading(true);
      const res = await custom(getWorkingHoursToday(detailId));
      const result = get(res, 'data.value[0].HrsPrDay', 0);
      const ikt = get(res, 'data.value[0].InKjarniTime', false);
      setInKjarniTime(ikt);
      setData(result);
      setLoading(false);
    };
    if (detailId) {
      fetchWorkingHours();
    }
  }, [detailId]);
  return { loading, data, inKjarniTime };
};
const THolidayRequest = ({
  loading, DefaultEmployeeDetailID, showSuccessNotification, showErrorNotification,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [warningText, setWarningText] = useState('');
  const [warningType, setWarningType] = useState('');
  const [timeError, setTimeError] = useState('');

  const [showHalfDayWarning, setShowHalfDayWarning] = useState(false);

  const [isConfirming, setIsConfirming] = useState(false);

  const validateAndSubmitRequest = async (data) => {
    const requestConfig = {
      url: '/kjarni/api/v2/ClockCardEntries/CreateEmployeeDetailApprovalRequest',
      method: 'POST',
      data,
    };
    try {
      const res = await custom(requestConfig);
      return res.data;
    } catch (error) {
      showErrorNotification(error);
      return null;
    }
  };

  const resetSubmitState = () => {
    setShowHalfDayWarning(false);
    setIsDisabled(false);
    setIsConfirming(false);
    setWarningText('');
    setWarningType('');
  };

  const openDialog = () => setShowDialog(true);
  const closeDialog = () => {
    resetSubmitState();
    setTimeError('');
    setShowDialog(false);
  };
  const { data: workingHours, inKjarniTime } = useWorkingHours(DefaultEmployeeDetailID);
  const {
    profileReducer: { employeeDetailOrgList },
    WebValuesReducer: { valuesByKey }
  } = useSelector(state => state);
  const {
    handleSubmit,
    watch,
    errors,
    control,
    setValue,
    getValues,
  } = useForm();
  const employeeDetailOrg = find(employeeDetailOrgList, d => d.EmployeeDetailID === DefaultEmployeeDetailID);
  const timeOfficerId = get(employeeDetailOrg, 'EmployeeOfficerTimeID', 0);
  const timeOfficerName = get(employeeDetailOrg, 'EmployeeOfficerTimeName', '');
  const showTimeOfficerSelect = get(valuesByKey, ['HolidayRequest.TimeOfficerApproval', 'Value']) === 'true';
  const managerName = get(employeeDetailOrg, 'ManagerName', '');
  const PeriodBegda = watch('PeriodBegda');
  const PeriodEndda = watch('PeriodEndda');
  const Duration = watch('Duration');
  const ConnectedID = watch('ConnectedID');

  const onSubmit = async (data) => {
    setIsDisabled(true);
    const payload = {
      EmployeeDetailID: DefaultEmployeeDetailID,
      Description: get(data, 'RequestDescription', ''),
      PeriodBegda: moment(get(data, 'PeriodBegda', new Date())).toISOString(),
      PeriodEndda: moment(get(data, 'PeriodEndda', new Date())).toISOString(),
      HoursPerDay: Number(get(data, 'Duration', 0)),
      ApproverID: get(data, 'RequestApproverID', 0),
      IsUnpaidVacation: data.ConnectedID === 1,
      IncludesWeekends: false,
      IncludesRedDays: false,
      UseAccrualsIfNeeded: isConfirming,
    };

    const result = await validateAndSubmitRequest(payload);
    const locale = localStorage.language === 'is' ? 'IS' : 'EN';
    const messages = result.Messages.filter(m => m.Language === locale);
    const formattedMessage = messages
      // eslint-disable-next-line
      .map((msg) => msg.Message.endsWith('.') ? msg.Message : `${msg.Message}.`)
      .join('\n');
    if (result && result.Status === OK) {
      closeDialog();
      showSuccessNotification();
    } else if (result && result.Status === WARNING) {
      setWarningText(formattedMessage);
      setWarningType('warning');
      setIsConfirming(true);
    } else if (result && result.Status === ERROR) {
      setWarningText(formattedMessage);
      setWarningType('error');
    }
    setIsDisabled(false);
  };

  useEffect(() => {
    resetSubmitState();
    if (PeriodBegda && PeriodEndda && Duration) {
      const isClockOutBeforeIn = moment(PeriodEndda).isBefore(moment(PeriodBegda));
      const isBeforeCurrentDate = moment(PeriodBegda).isBefore(moment(), 'day');
      if (isBeforeCurrentDate) {
        setTimeError('holidayApprovalRequest.timeError.beforeCurrentDate');
      } else if (isClockOutBeforeIn) {
        setTimeError('holidayApprovalRequest.timeError');
      } else {
        setTimeError('');
      }
    }
  }, [PeriodBegda, PeriodEndda, Duration, ConnectedID]);

  const hasHalfDays = (startDate, endDate) => {
    if (!inKjarniTime) {
      return false;
    }
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      if (isHalfDay(currentDate)) {
        return true;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return false;
  };
  useEffect(() => {
    if (PeriodBegda && PeriodEndda) {
      setShowHalfDayWarning(hasHalfDays(moment(PeriodBegda), moment(PeriodEndda)));
    }

  }, [PeriodBegda, PeriodEndda]);

  const actions = [
    <FlatButton
      label={<FormattedMessage id="alert.cancel" />}
      disabled={loading}
      secondary
      onClick={closeDialog}
    />,
    <RaisedButton
      label={<FormattedMessage id={isConfirming ? 'alert.ok' : 'submit'} />}
      icon={<FontIcon className="material-icons">send</FontIcon>}
      disabled={loading || !isEmpty(errors) || isDisabled}
      primary
      onClick={handleSubmit(onSubmit)}
    />
  ];
  return (
    <div style={styles.container} data-cy="holiday-request">
      <Dialog
        open={showDialog}
        actions={actions}
        contentStyle={{
          overflow: 'hidden',
          maxWidth: 500,
          width: '100%',
        }}
        bodyStyle={{
          overflowY: 'auto',
        }}
        modal
      >
        {loading ? (
          <div
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.34)',
              position: 'absolute',
              zIndex: 10,
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Paper
              style={{
                padding: 32,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <CircularProgress style={{ marginBottom: 24 }} size={100} />
              <FormattedMessage id="sending.message" />
            </Paper>
          </div>
        ) : null}
        <KTitle
          style={{
            marginLeft: 0,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
          }}
          title={(
            <React.Fragment>
              <FontIcon
                style={{
                  marginRight: 6, fontSize: 28, color: 'inherit',
                }}
                className="material-icons"
              >school
              </FontIcon>
              <FormattedMessage id="dash.title.holidayApprovalRequest" />
            </React.Fragment>
          )}
          subtitleStyle={{ margin: 0, fontSize: 12, lineHeight: '1' }}
          subtitle={(
            <React.Fragment>
              <FormattedMessage id="holidayApprovalRequest.description" />
            </React.Fragment>
          )}
        />
        <form style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Controller
              as={(
                <TextField
                  fullWidth
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.description" />}
                  errorText={errors.RequestDescription ? <FormattedMessage id="editPanel.required" /> : ''}
                  multiLine
                  rows={3}
                />
              )}
              name="RequestDescription"
              control={control}
              defaultValue=""
              rules={{ required: true }}
            />
            <Controller
              render={({
                onChange, onBlur, value, name, ref,
              }) => (
                <TextField
                  name={name}
                  inputRef={ref}
                  fullWidth
                  type="date"
                  value={value}
                  onChange={e => onChange(e.target.value)}
                  onBlur={(e) => {
                    onBlur();
                    const currentEndda = getValues('PeriodEndda');
                    if (e.target.value && !currentEndda) {
                      setValue('PeriodEndda', e.target.value);
                    }
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.from" />}
                  errorText={errors.PeriodBegda ? <FormattedMessage id="editPanel.required" /> : ''}
                />
              )}
              name="PeriodBegda"
              control={control}
              defaultValue=""
              rules={{ required: true }}
            />
            <Controller
              as={(
                <TextField
                  fullWidth
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.to" />}
                  errorText={errors.PeriodEndda ? <FormattedMessage id="editPanel.required" /> : ''}
                />
              )}
              name="PeriodEndda"
              control={control}
              defaultValue=""
              rules={{ required: true }}
            />
            {showHalfDayWarning && (
              <div style={warningStyle}><FormattedMessage id="time.HalfDayWarning.Description" /></div>
            )}
            {timeError && <div style={errorStyle}><FormattedMessage id={timeError} /></div>}
            <Controller
              render={({
                onChange, onBlur, value, name, ref,
              }) => (
                <SelectField
                  name={name}
                  onBlur={onBlur}
                  inputRef={ref}
                  onChange={(_, __, val) => onChange(val)}
                  fullWidth
                  floatingLabelFixed
                  value={value}
                  floatingLabelText={<FormattedMessage id="time.Type" />}
                >
                  <MenuItem primaryText={<FormattedMessage id="holiday" />} value={0} key={0} />
                  <MenuItem primaryText={<FormattedMessage id="holiday.unpaid" />} value={1} key={1} />
                </SelectField>
              )}
              name="ConnectedID"
              control={control}
              defaultValue={0}
            />
            <Controller
              as={(
                <TextField
                  fullWidth
                  type="number"
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.hoursPerDay" />}
                  min={0}
                  step={0.01}
                />
              )}
              defaultValue={workingHours}
              name="Duration"
              control={control}
              errorText={warningText}
              errorStyle={{ ...(warningType === 'warning' && { color: pallet.warningColor }) }}
              rules={{ required: true }}
            />
            {(showTimeOfficerSelect && timeOfficerId) ?
              <Controller
                render={({
                  onChange, onBlur, value, name, ref,
                }) => (
                  <SelectField
                    name={name}
                    onBlur={onBlur}
                    inputRef={ref}
                    onChange={(_, __, val) => onChange(val)}
                    fullWidth
                    floatingLabelFixed
                    value={value}
                    floatingLabelText={<FormattedMessage id="time.Approver" />}
                  >
                    <MenuItem primaryText={managerName} value={0} key={0} />
                    <MenuItem primaryText={timeOfficerName} value={timeOfficerId} key={timeOfficerId} />
                  </SelectField>
                )}
                name="RequestApproverID"
                control={control}
                defaultValue={timeOfficerId}
              /> : null}
          </div>
        </form>
      </Dialog>
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* <WomanLaptop /> */}
        <Thinking />
        <div
          style={{
            padding: 8,
            marginBottom: 12,
            marginTop: 12,
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <FontIcon style={{ marginRight: 8 }} color={pallet.infoColor} className="material-icons" >info</FontIcon>
          <FormattedMessage id="holidayApprovalRequest.description" />
        </div>
        <RaisedButton
          primary
          style={{ marginBottom: 12 }}
          onClick={openDialog}
          data-cy="holiday-request-button"
          label={<FormattedMessage id="holidayApprovalRequest.button" />}
        />
      </div>
    </div>
  );
};

THolidayRequest.propTypes = {
  loading: PropTypes.bool.isRequired,
  showSuccessNotification: PropTypes.func.isRequired,
  showErrorNotification: PropTypes.func.isRequired,
  DefaultEmployeeDetailID: PropTypes.number.isRequired,
};

THolidayRequest.defaultProps = {
};

export default connect(
  ({ approvalRequestReducer, loginReducer }) => ({
    ...approvalRequestReducer,
    DefaultEmployeeDetailID: get(loginReducer, 'user.DefaultEmployeeDetailID', 0),
  }),
  dispatch => ({
    showSuccessNotification: () => dispatch(addNotification({
      title: <FormattedMessage id="approvalRequest.create.success" />,
      status: 'success',
      dismissible: true,
      dismissAfter: 5000,
    })),
    showErrorNotification: err => dispatch(addNotification({
      title: <FormattedMessage id="notification.error.title" />,
      message: err.message || 'Villa kom upp við að stofna færslu í kjarna',
      status: 'error',
      dismissible: true,
      dismissAfter: 5000,
    })),
  }),
)(THolidayRequest);



// WEBPACK FOOTER //
// ./src/containers/tiles/THolidayRequest.js